import useSchoolSearchFiltersStore, { type SchoolSearchState } from '@store/SchoolSearchFiltersStore';
import useMajorSearchStore, { type MajorSearchState } from '@store/MajorSearchStore';
import useScholarshipListStore, { type ScholarshipListFilters } from '@store/AllScholarshipsStore';
import useStateSpotlightStore from '@store/StateSpotlightStore';

import { REST_OF_US_FILTER, STATE_PUBLIC_COLLEGE_FILTER } from '@constants/stateSpotlight';

import { StateSpotlightPage } from '@interfaces/stateSpotlight';

export type ExploreTab = StateSpotlightPage;

const KEYWORDS_FILTER = 'keywords';

const LOCATION_FILTERS = [STATE_PUBLIC_COLLEGE_FILTER, REST_OF_US_FILTER];

const EXPLORE_TAB_FILTERS: Record<ExploreTab, string[]> = {
  majors: LOCATION_FILTERS,
  schools: [...LOCATION_FILTERS, KEYWORDS_FILTER],
  scholarships: [...LOCATION_FILTERS, KEYWORDS_FILTER],
};

const getTabFilters = (tab: ExploreTab): Record<string, unknown> => {
  if (tab === 'schools') return useSchoolSearchFiltersStore.getState().filters as Record<string, unknown>;
  if (tab === 'majors') return useMajorSearchStore.getState().filters as Record<string, unknown>;

  return useScholarshipListStore.getState().filters as Record<string, unknown>;
};

const setTabFilters = (tab: ExploreTab, filters: Record<string, unknown>) => {
  if (tab === 'schools') useSchoolSearchFiltersStore.getState().setSearchFilters(filters as SchoolSearchState);
  else if (tab === 'majors') useMajorSearchStore.getState().setSearchFilters(filters as Partial<MajorSearchState>);
  else useScholarshipListStore.getState().setScholarshipFilters(filters as ScholarshipListFilters);
};

const removeTabFilter = (tab: ExploreTab, key: string) => {
  if (tab === 'schools') useSchoolSearchFiltersStore.getState().removeSearchFilter(key as keyof SchoolSearchState);
  else if (tab === 'majors') useMajorSearchStore.getState().removeSearchFilter(key as keyof MajorSearchState);
  else useScholarshipListStore.getState().removeScholarshipFilter(key as keyof ScholarshipListFilters);
};

const hasValue = (value: unknown) => (Array.isArray(value) ? value.length > 0 : !!value);

const hasSettledLocationFilters = (tab: ExploreTab) => {
  const { isLoaded, spotlight, appliedDefaults } = useStateSpotlightStore.getState();

  return appliedDefaults[tab] || (isLoaded && !spotlight);
};

export const carryExploreFilters = (from: ExploreTab, to: ExploreTab) => {
  if (from === to) return;

  const source = getTabFilters(from);
  const target = getTabFilters(to);
  const canCarryLocation = hasSettledLocationFilters(from);
  const sharedKeys = EXPLORE_TAB_FILTERS[to].filter(
    (key) => EXPLORE_TAB_FILTERS[from].includes(key) && (canCarryLocation || !LOCATION_FILTERS.includes(key)),
  );
  const carried = Object.fromEntries(
    sharedKeys.filter((key) => hasValue(source[key])).map((key) => [key, source[key]]),
  );

  sharedKeys.filter((key) => !hasValue(source[key]) && key in target).forEach((key) => removeTabFilter(to, key));
  setTabFilters(to, { ...carried, skip: 0 });

  if (canCarryLocation) useStateSpotlightStore.getState().markFiltersCarried(to);
};
